import React, { useEffect, useState, useCallback, useRef } from "react";
import { Header } from "@/components/Header";
import { FacilityCard } from "@/components/FacilityCard";
import { MapView } from "@/components/MapView";
import { MapErrorBoundary } from "@/components/MapErrorBoundary";
import { ClaimSubmitDialog } from "@/components/ClaimSubmitDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Map as MapIcon, List, Building2 } from "lucide-react";

export default function ClaimPage() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counties, setCounties] = useState([]);
  const [center, setCenter] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [mobileMap, setMobileMap] = useState(false);

  const [search, setSearch] = useState("");
  const [county, setCounty] = useState("all");
  const [radius, setRadius] = useState("any");

  const [claimFacility, setClaimFacility] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);

  const fetchFacilities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/facilities", {
        params: {
          search: search || undefined, county,
          radius_miles: radius !== "any" && search ? Number(radius) : undefined,
        },
      });
      setFacilities(data.facilities || []);
      setCenter(data.center || null);
    } catch { toast.error("Failed to load facilities"); }
    finally { setLoading(false); }
  }, [search, county, radius]);

  const debounced = useRef(null);
  useEffect(() => {
    if (debounced.current) clearTimeout(debounced.current);
    debounced.current = setTimeout(fetchFacilities, 300);
    return () => clearTimeout(debounced.current);
  }, [fetchFacilities]);
  useEffect(() => { api.get("/facilities/counties").then((r) => setCounties(r.data)).catch((e) => console.error("Counties load failed", e)); }, []);

  const openClaim = (f) => {
    if (f.claimed || f.claim_status === "approved") { toast.info("This facility has already been claimed."); return; }
    if (f.claim_status === "pending") { toast.info("A claim for this facility is already under review."); return; }
    setClaimFacility(f);
    setClaimOpen(true);
  };

  const onClaimClosed = (v) => { setClaimOpen(v); if (!v) fetchFacilities(); };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />

      {/* Banner */}
      <div className="bg-[#1A365D] text-white" data-testid="claim-banner">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-5 flex items-start gap-3">
          <Building2 className="h-6 w-6 shrink-0 mt-0.5 text-[#7FC3E8]" />
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-medium">Are you a facility director or administrator?</h1>
            <p className="text-sm md:text-base text-white/80 mt-0.5">Find your facility below and claim it in under a minute.</p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="sticky top-16 md:top-20 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E8E6DF]">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6660]" />
            <Input data-testid="claim-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchFacilities(); } }}
              placeholder="Search your facility by name or city..." className="pl-9 rounded-xl border-[#E8E6DF] h-11" />
          </div>
          <div className="grid grid-cols-2 gap-3 w-full sm:contents">
            <Select value={county} onValueChange={setCounty}>
              <SelectTrigger className="w-full sm:w-[170px] rounded-xl border-[#E8E6DF] h-11" data-testid="claim-county-filter" aria-label="Filter by county"><SelectValue placeholder="County" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All counties</SelectItem>
                {counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={radius} onValueChange={setRadius}>
              <SelectTrigger className="w-full sm:w-[150px] rounded-xl border-[#E8E6DF] h-11" data-testid="claim-radius-filter" aria-label="Filter by distance"><SelectValue placeholder="Radius" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any distance</SelectItem>
                <SelectItem value="5">Within 5 mi</SelectItem>
                <SelectItem value="10">Within 10 mi</SelectItem>
                <SelectItem value="25">Within 25 mi</SelectItem>
                <SelectItem value="50">Within 50 mi</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Split panel */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12">
        <div className={`md:col-span-5 lg:col-span-4 md:h-[calc(100vh-200px)] md:overflow-y-auto p-4 pb-28 md:pb-6 ${mobileMap ? "hidden md:block" : "block"}`}>
          <p className="text-sm text-[#4A534E] mb-4" data-testid="claim-results-count">
            {loading ? "Searching..." : <><span className="font-semibold text-[#1C211F]">{facilities.length}</span> facilities</>}
          </p>
          <div className="space-y-4">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-56 w-full rounded-2xl" />)
              : facilities.map((f) => (
                  <FacilityCard key={f.facility_id} facility={f} active={hoverId === f.facility_id}
                    claimMode onClaim={openClaim} onSelect={openClaim} onHover={setHoverId} />
                ))}
            {!loading && facilities.length === 0 && (
              <div className="text-center py-16 text-[#5C6660]">No facilities match your search.</div>
            )}
          </div>
        </div>

        <div className={`md:col-span-7 lg:col-span-8 h-[70vh] md:h-[calc(100vh-200px)] md:sticky md:top-[200px] ${mobileMap ? "block" : "hidden md:block"}`}>
          <MapErrorBoundary>
            <MapView facilities={facilities} activeId={hoverId} center={center} radiusMiles={radius !== "any" ? Number(radius) : null} onSelect={openClaim} onHover={setHoverId} />
          </MapErrorBoundary>
        </div>
      </div>

      <button onClick={() => setMobileMap(!mobileMap)} data-testid="claim-map-toggle"
        className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-[#1A365D] text-white shadow-lg backdrop-blur">
        {mobileMap ? <><List className="h-4 w-4" /> List</> : <><MapIcon className="h-4 w-4" /> Map</>}
      </button>

      <ClaimSubmitDialog facility={claimFacility} open={claimOpen} onOpenChange={onClaimClosed} />
    </div>
  );
}
